import React, { useMemo, useState } from "react";
import { CATEGORIES, CATEGORY_COLOR, PUBLIC_STATUS_STYLE, apiInquireOnListing } from "./api";
import { CustomIcon } from "../shared/CustomIcon";

// PublicListings — card grid grouped/filterable by category.
export default function PublicListings({ listings, dept, currentUser, users, addNotification }) {
  const [catFilter, setCatFilter] = useState("");
  const [inquiredId, setInquiredId] = useState(null);
  const [inquireError, setInquireError] = useState("");

  const filtered = useMemo(() => (
    catFilter ? listings.filter(l => l.category === catFilter) : listings
  ), [listings, catFilter]);

  const inquire = async (listing) => {
    setInquireError("");
    try {
      // Server-side endpoint resolves the leader + fires the notification
      // with the canonical text required by Phase 2.
      await apiInquireOnListing(listing.id);
      setInquiredId(listing.id);
      setTimeout(() => setInquiredId(prev => prev === listing.id ? null : prev), 3500);
    } catch (e) {
      setInquireError("Could not send your inquiry — please try again in a moment.");
    }
  };

  return (
    <div data-testid="donations-public">
      <div className="don-toolbar" style={{ marginBottom: 18 }}>
        <div className="don-toolbar-filters">
          <span className="don-filter-label">Filter</span>
          <select className="don-filter-select" data-testid="pl-cat-filter" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
          {filtered.length} item{filtered.length === 1 ? "" : "s"} available
        </div>
      </div>
      {inquireError && (
        <div data-testid="pl-inquire-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.40)", borderRadius: 10, color: "#ff9b9b", padding: "10px 12px", marginBottom: 14, fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>{inquireError}</div>
      )}

      <div className="pl-grid" data-testid="pl-grid">
        {filtered.length === 0 && (
          <div className="pl-empty" data-testid="pl-empty">
            {listings.length === 0
              ? "No public listings yet. Mark an intake as “Show on public listing page” to make it appear here."
              : `No items match category “${catFilter}”.`}
          </div>
        )}
        {filtered.map(l => {
          const catColor = CATEGORY_COLOR[l.category] || "var(--kc-accent)";
          const sStyle = PUBLIC_STATUS_STYLE[l._publicStatus] || PUBLIC_STATUS_STYLE.Available;
          const buttonDisabled = l._publicStatus !== "Available";
          return (
            <article key={l.id} className="pl-card" data-testid={`pl-card-${l.id}`}>
              <div className="pl-card-photo">
                {l.photo ? <img src={l.photo} alt={l.itemName} /> : <CustomIcon name="gift" size={24} />}
              </div>
              <div className="pl-card-body">
                <div className="pl-card-head">
                  <span className="pl-cat-badge" style={{ background: `${catColor}1F`, color: catColor }}>{l.category}</span>
                  <span className="pl-status-badge" data-testid={`pl-status-${l.id}`} style={{ background: sStyle.bg, color: sStyle.color, borderColor: sStyle.border }}>
                    {l._publicStatus}
                  </span>
                </div>
                <div className="pl-card-title">{l.itemName}</div>
                <div className="pl-card-desc">{l.shortDescription || "No description provided."}</div>
                <div className="pl-card-btn">
                  {inquiredId === l.id ? (
                    <button type="button" className="btn btn-sm btn-outline w-full" data-testid={`pl-contact-confirm-${l.id}`} disabled style={{ justifyContent: "center", color: "#4ade80", borderColor: "rgba(74,222,128,0.45)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <CustomIcon name="check" size={13} /> Department leader notified
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-gold w-full"
                      data-testid={`pl-contact-btn-${l.id}`}
                      onClick={() => inquire(l)}
                      disabled={buttonDisabled}
                      style={{ justifyContent: "center", opacity: buttonDisabled ? 0.55 : 1 }}
                    >
                      {buttonDisabled ? (l._publicStatus === "Claimed" ? "Already claimed" : "Distributed") : "Contact Department Leader"}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
